import type { CityPriceMapItem, MarketSummary } from "@/lib/api";
import { TURKEY_PROVINCES } from "@/lib/turkey-geo";

/** İl eşlemesi, satır kurma ve fiyat endeksi dilimleri — Türkiye fiyat haritaları için ortak. */

const CITY_ALIASES: Record<string, string> = {
  afyon: "afyonkarahisar", içel: "mersin", icel: "mersin", maraş: "kahramanmaraş",
  "k maraş": "kahramanmaraş", "k. maraş": "kahramanmaraş", urfa: "şanlıurfa", sanliurfa: "şanlıurfa",
};

export function cityKey(value: string): string {
  const n = value.trim().toLocaleLowerCase("tr-TR").replace(/\s+/g, " ");
  return CITY_ALIASES[n] ?? n;
}

export interface ProvinceRow {
  code: string;
  name: string;
  d: string;
  markets: MarketSummary[];
  price: CityPriceMapItem | null;
}

export function buildProvinceRows(markets: MarketSummary[], prices: CityPriceMapItem[]): ProvinceRow[] {
  const marketMap = new Map<string, MarketSummary[]>();
  for (const m of markets) {
    if (!m.cityName) continue;
    const k = cityKey(m.cityName);
    marketMap.set(k, [...(marketMap.get(k) ?? []), m]);
  }
  const priceMap = new Map(prices.map((p) => [cityKey(p.cityName), p]));
  return TURKEY_PROVINCES.map((p) => ({
    code: p.code, name: p.name, d: p.d,
    markets: marketMap.get(cityKey(p.name)) ?? [],
    price: priceMap.get(cityKey(p.name)) ?? null,
  }));
}

/** priceIndex: 1.00 = Türkiye sepet ortalaması. */
export function formatIndex(idx: number | null | undefined): string {
  if (!Number.isFinite(idx ?? NaN)) return "-";
  return `${(idx as number).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
}

export function indexLabel(idx: number | null | undefined): string {
  if (!Number.isFinite(idx ?? NaN)) return "veri yok";
  const pct = Math.round(((idx as number) - 1) * 100);
  if (pct === 0) return "Türkiye ortalamasında";
  return pct > 0 ? `Türkiye ortalamasının %${pct} üzerinde` : `Türkiye ortalamasının %${Math.abs(pct)} altında`;
}

/**
 * Sabit dilimler (2026-09-30). Eskiden renk gözlenen en düşük–en yüksek aralığın üçte biriydi:
 * "sarı" ortalamada değil aralığın ortasında demekti, skala okunmuyordu. Dilim sınırı
 * Türkiye ortalamasına göre sabittir; aynı renk her gün aynı anlama gelir.
 */
export const PRICE_BINS = [
  { key: "ucuz2", label: "%15'ten fazla ucuz", short: "< 0,85×", max: 0.85, color: "hsl(140 62% 30%)" },
  { key: "ucuz1", label: "%5–15 ucuz", short: "0,85–0,95×", max: 0.95, color: "hsl(128 48% 52%)" },
  { key: "orta", label: "Ortalama (±%5)", short: "0,95–1,05×", max: 1.05, color: "hsl(48 90% 55%)" },
  { key: "pahali1", label: "%5–15 pahalı", short: "1,05–1,15×", max: 1.15, color: "hsl(24 88% 54%)" },
  { key: "pahali2", label: "%15'ten fazla pahalı", short: "≥ 1,15×", max: Infinity, color: "hsl(2 70% 46%)" },
] as const;
export type PriceBinKey = (typeof PRICE_BINS)[number]["key"] | "yok";

export function binFor(idx: number | null | undefined): PriceBinKey {
  if (!Number.isFinite(idx ?? NaN)) return "yok";
  return PRICE_BINS.find((b) => (idx as number) < b.max || b.max === Infinity)!.key;
}

export function binColor(key: PriceBinKey): string {
  return key === "yok" ? "var(--map-empty-fill)" : PRICE_BINS.find((b) => b.key === key)!.color;
}
